import { useState, useCallback } from 'react';
import { Bot, User, Copy, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { safeString } from '@/lib/utils';
import MarkdownRenderer from './MarkdownRenderer';
import TypingIndicator from './TypingIndicator';

export default function MessageBubble({ role, content, isStreaming }) {
  const { t } = useTranslation('chat');
  const isUser = role === 'user';
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(typeof content === 'string' ? content : safeString(content)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [content]);

  return (
    <div className={`group flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar */}
      <Avatar className="mt-0.5 shrink-0">
        <AvatarFallback
          className={
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground'
          }
        >
          {isUser ? <User className="size-4" /> : <Bot className="size-4" />}
        </AvatarFallback>
      </Avatar>

      {/* Message */}
      <div className="relative max-w-[80%] min-w-0">
        <div
          className={`rounded-2xl px-4 py-2.5 ${
            isUser
              ? 'bg-primary text-primary-foreground rounded-br-md'
              : 'bg-muted text-foreground rounded-bl-md'
          }`}
        >
          {isUser ? (
            <div className="text-sm whitespace-pre-wrap">{typeof content === 'string' ? content : safeString(content)}</div>
          ) : isStreaming && !content ? (
            <TypingIndicator />
          ) : (
            <div className="text-sm prose-sm">
              <MarkdownRenderer content={content} />
              {isStreaming && (
                <span className="inline-block w-1.5 h-4 ml-0.5 bg-foreground/70 animate-pulse rounded-sm align-text-bottom" />
              )}
            </div>
          )}
        </div>

        {/* Copy button on hover */}
        {!isStreaming && content && !isUser && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute -bottom-1 right-0 size-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={handleCopy}
                >
                  {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{copied ? t('copied', 'Copied!') : t('copy', 'Copy')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}
